import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowRight, Loader2, RefreshCw, FileText, TrendingUp,
  ListTodo, ChevronDown, Mail, AlertCircle, CheckCircle2, Zap,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage helper for the email gate (per browser session)
const EMAIL_KEY = 'qindex_demo_email';
const getStoredEmail = () => sessionStorage.getItem(EMAIL_KEY) || '';
const setStoredEmail = (e) => sessionStorage.setItem(EMAIL_KEY, e);

// ============================================================
// EMAIL GATE
// ============================================================
function EmailGate({ open, onClose, onSubmit, context }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid work email');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(email.trim());
      setStoredEmail(email.trim());
    } catch (err) {
      setError(err?.message || 'Could not generate. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1411]/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white max-w-md w-full rounded-sm shadow-xl border border-[#E8E4DF] p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="email-gate-modal"
      >
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[#A86A3A]" />
          <span className="text-xs uppercase tracking-widest text-[#A86A3A]">{context}</span>
        </div>
        <h3 className="text-2xl font-light text-[#1A1411] mb-2">One quick step.</h3>
        <p className="text-[#6B5B52] text-sm mb-6 leading-relaxed">
          Enter your work email and we'll generate the brief now. We'll also send you a copy you can share internally.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="relative mb-3">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9B8B7E]" />
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@firm.com"
              className="w-full border border-[#E8E4DF] rounded-sm pl-10 pr-3 py-3 text-[#1A1411] focus:outline-none focus:border-[#A86A3A]"
              data-testid="email-gate-input"
            />
          </div>
          {error && (
            <p className="text-xs text-red-600 mb-3 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting || !email}
            className="w-full bg-[#A86A3A] text-white py-3 rounded-sm font-medium hover:bg-[#8B5730] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="email-gate-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <>Generate Now <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-[10px] text-[#9B8B7E] mt-3 text-center">No spam. We'll only follow up if you ask us to.</p>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// COMMON OUTPUT WRAPPER
// ============================================================
function OutputShell({ title, subtitle, generatedAt, isLive, onRegenerate, regenerating, children, testId }) {
  return (
    <div className="bg-white border border-[#E8E4DF] rounded-sm overflow-hidden" data-testid={testId}>
      <div className="border-b border-[#E8E4DF] px-6 py-4 flex items-start justify-between gap-4 bg-[#FAF8F5]">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-[#A86A3A] mb-0.5">{title}</p>
          <h4 className="text-[#1A1411] font-medium truncate">{subtitle}</h4>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isLive && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[#A86A3A] bg-[#A86A3A]/10 px-2 py-1 rounded-sm">
              <Zap className="w-3 h-3" /> Live AI
            </span>
          )}
          {onRegenerate && (
            <button
              onClick={onRegenerate}
              disabled={regenerating}
              className="inline-flex items-center gap-1 text-[11px] text-[#6B5B52] hover:text-[#A86A3A] border border-[#E8E4DF] hover:border-[#A86A3A] px-2.5 py-1 rounded-sm transition-colors disabled:opacity-60"
              data-testid={`${testId}-regenerate`}
              title="Refresh with live AI"
            >
              {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Re-generate
            </button>
          )}
        </div>
      </div>
      <div className="px-6 py-6 max-h-[640px] overflow-y-auto">
        {children}
      </div>
      {generatedAt && (
        <div className="border-t border-[#E8E4DF] px-6 py-3 bg-[#FAF8F5] text-[10px] text-[#9B8B7E]">
          Generated {new Date(generatedAt).toLocaleString()} · Showcase data — for demonstration purposes
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="mb-6 last:mb-0">
      <p className="text-[10px] uppercase tracking-widest text-[#A86A3A] mb-2">{label}</p>
      <div className="text-sm text-[#1A1411] leading-relaxed">{children}</div>
    </div>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2.5 text-[#3D3530]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#A86A3A] mt-1.5 flex-shrink-0" />
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

// ============================================================
// 1. AI CLIENT MEETING BRIEF (HERO)
// ============================================================
function MeetingBriefModule() {
  const [families, setFamilies] = useState([]);
  const [familyId, setFamilyId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailGate, setEmailGate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/public/showcase/families`)
      .then(r => r.json())
      .then(d => {
        setFamilies(d.families || []);
        if (d.families?.length) setFamilyId(d.families[0].id);
      })
      .catch(() => {});
  }, []);

  const generate = async (email) => {
    setLoading(true);
    setEmailGate(false);
    try {
      const r = await fetch(`${API_BASE}/public/showcase/meeting-brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ familyId, email }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      const data = await r.json();
      // Smooth UX delay so the loading animation registers
      setTimeout(() => { setResult(data); setLoading(false); }, 900);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const handleClick = () => {
    if (!familyId) return;
    const stored = getStoredEmail();
    if (stored) generate(stored);
    else setEmailGate(true);
  };

  const regenerate = async () => {
    if (!result) return;
    const email = getStoredEmail();
    if (!email) return;
    setRegenerating(true);
    try {
      const r = await fetch(`${API_BASE}/public/showcase/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'meeting-brief', key: familyId, email }),
      });
      const data = await r.json();
      setResult(prev => ({ ...prev, brief: data.result, isLive: data.isLive, generatedAt: data.generatedAt }));
    } catch { /* keep existing */ }
    setRegenerating(false);
  };

  const fmtCurrency = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="grid lg:grid-cols-5 gap-8" data-testid="meeting-brief-module">
      {/* INPUT */}
      <div className="lg:col-span-2">
        <div className="sticky top-24 bg-white border border-[#E8E4DF] rounded-sm p-6">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A86A3A] mb-3">
            <Sparkles className="w-3 h-3" /> Hero feature
          </div>
          <h3 className="text-2xl font-light text-[#1A1411] mb-2">AI Client Meeting Brief</h3>
          <p className="text-[#6B5B52] text-sm mb-6 leading-relaxed">
            Generate a complete pre-meeting brief from a single client record — portfolio snapshot, key developments, risks, opportunities, and a draft talking-point script.
          </p>

          <label className="block text-xs uppercase tracking-widest text-[#9B8B7E] mb-2">Select Showcase Family</label>
          <div className="relative mb-4">
            <select
              value={familyId}
              onChange={(e) => { setFamilyId(e.target.value); setResult(null); }}
              className="w-full appearance-none bg-white border border-[#E8E4DF] rounded-sm px-3 py-3 pr-10 text-[#1A1411] focus:outline-none focus:border-[#A86A3A]"
              data-testid="meeting-brief-family-select"
            >
              {families.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#9B8B7E]" />
          </div>

          <button
            onClick={handleClick}
            disabled={loading || !familyId}
            className="w-full bg-[#1A1411] text-white py-3 rounded-sm font-medium hover:bg-[#A86A3A] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="meeting-brief-generate-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating brief…</> : <><FileText className="w-4 h-4" /> Generate Meeting Brief</>}
          </button>
          <p className="text-[10px] text-[#9B8B7E] mt-3 leading-relaxed">
            Uses your existing client data, structures and activity. <br />
            <span className="text-[#A86A3A]">Demo runs on three curated showcase families.</span>
          </p>
        </div>
      </div>

      {/* OUTPUT */}
      <div className="lg:col-span-3">
        {!result && !loading && (
          <div className="border border-dashed border-[#E8E4DF] rounded-sm p-12 text-center bg-[#FAF8F5]">
            <FileText className="w-10 h-10 text-[#A86A3A]/40 mx-auto mb-3" />
            <p className="text-[#6B5B52] text-sm">Pick a family and generate to see a complete pre-meeting brief.</p>
          </div>
        )}
        {loading && (
          <div className="border border-[#E8E4DF] rounded-sm p-12 text-center bg-white">
            <Loader2 className="w-8 h-8 text-[#A86A3A] mx-auto mb-3 animate-spin" />
            <p className="text-[#1A1411] font-medium">Generating brief…</p>
            <p className="text-xs text-[#9B8B7E] mt-1">Synthesising portfolio, recent activity, and talking points</p>
          </div>
        )}
        {result && (
          <OutputShell
            title="Client Meeting Brief"
            subtitle={result.family.name}
            generatedAt={result.generatedAt}
            isLive={result.isLive}
            onRegenerate={regenerate}
            regenerating={regenerating}
            testId="meeting-brief-output"
          >
            <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-[#E8E4DF]">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Total Portfolio</p>
                <p className="text-xl text-[#1A1411] font-light">{fmtCurrency(result.portfolio.totalValue)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Allocation</p>
                <p className="text-sm text-[#1A1411]">G {result.portfolio.allocation.growth}% / D {result.portfolio.allocation.defensive}% / A {result.portfolio.allocation.alternatives}%</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">1YR Performance</p>
                <p className="text-xl text-[#1A1411] font-light">+{result.portfolio.performance.oneYear}%</p>
              </div>
            </div>

            <Section label="Summary"><p className="text-[#3D3530]">{result.brief.summary}</p></Section>
            <Section label="Key Developments"><Bullets items={result.brief.keyDevelopments} /></Section>
            <Section label="Risks & Considerations"><Bullets items={result.brief.risks} /></Section>
            <Section label="Opportunities"><Bullets items={result.brief.opportunities} /></Section>
            <Section label="Suggested Talking Points"><Bullets items={result.brief.talkingPoints} /></Section>
            <Section label="Action Items"><Bullets items={result.brief.actionItems} /></Section>

            <div className="flex flex-col sm:flex-row gap-3 pt-6 mt-6 border-t border-[#E8E4DF]">
              <Link to="/pilot" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#A86A3A] text-white px-5 py-3 rounded-sm font-medium hover:bg-[#8B5730] transition-colors text-sm" data-testid="brief-cta-pilot">
                Request a Pilot <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="flex-1 inline-flex items-center justify-center gap-2 border border-[#A86A3A] text-[#A86A3A] px-5 py-3 rounded-sm font-medium hover:bg-[#A86A3A]/5 transition-colors text-sm" data-testid="brief-cta-walkthrough">
                Book a Walkthrough
              </Link>
            </div>
          </OutputShell>
        )}
      </div>

      <EmailGate
        open={emailGate}
        onClose={() => setEmailGate(false)}
        onSubmit={generate}
        context="AI Client Meeting Brief"
      />
    </div>
  );
}

// ============================================================
// 2. AI PIPELINE SUMMARY
// ============================================================
function PipelineModule() {
  const [filter, setFilter] = useState('all');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailGate, setEmailGate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const generate = async (email) => {
    setLoading(true);
    setEmailGate(false);
    try {
      const r = await fetch(`${API_BASE}/public/showcase/pipeline-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filter, email }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      const data = await r.json();
      setTimeout(() => { setResult(data); setLoading(false); }, 800);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const handleClick = () => {
    const stored = getStoredEmail();
    if (stored) generate(stored);
    else setEmailGate(true);
  };

  const regenerate = async () => {
    const email = getStoredEmail();
    if (!email) return;
    setRegenerating(true);
    try {
      const r = await fetch(`${API_BASE}/public/showcase/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'pipeline', key: filter, email }),
      });
      const data = await r.json();
      setResult(prev => ({ ...prev, summary: data.result, isLive: data.isLive, generatedAt: data.generatedAt }));
    } catch { /* ignore */ }
    setRegenerating(false);
  };

  return (
    <div data-testid="pipeline-module">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A86A3A] mb-2">
            <TrendingUp className="w-3 h-3" /> Revenue intelligence
          </div>
          <h3 className="text-2xl font-light text-[#1A1411]">AI Pipeline Summary</h3>
          <p className="text-[#6B5B52] text-sm mt-1">Where the revenue is sitting and what it needs from your team this week.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setResult(null); }}
            className="appearance-none bg-white border border-[#E8E4DF] rounded-sm px-3 py-2.5 text-sm pr-8 text-[#1A1411] focus:outline-none focus:border-[#A86A3A]"
            data-testid="pipeline-filter"
          >
            <option value="all">All Leads</option>
            <option value="hot">Hot</option>
            <option value="warm">Warm</option>
            <option value="cold">Cold</option>
          </select>
          <button
            onClick={handleClick}
            disabled={loading}
            className="bg-[#1A1411] text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#A86A3A] transition-colors flex items-center gap-2 disabled:opacity-60"
            data-testid="pipeline-generate-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <>Generate Summary <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>

      {!result && !loading && (
        <div className="border border-dashed border-[#E8E4DF] rounded-sm p-10 text-center bg-[#FAF8F5]">
          <TrendingUp className="w-10 h-10 text-[#A86A3A]/40 mx-auto mb-3" />
          <p className="text-[#6B5B52] text-sm">Generate a pipeline summary to see priorities, risks and recommended actions.</p>
        </div>
      )}
      {loading && (
        <div className="border border-[#E8E4DF] rounded-sm p-10 text-center bg-white">
          <Loader2 className="w-7 h-7 text-[#A86A3A] mx-auto mb-3 animate-spin" />
          <p className="text-[#1A1411] font-medium">Generating pipeline summary…</p>
        </div>
      )}
      {result && (
        <OutputShell
          title="Pipeline Summary"
          subtitle={`${filter === 'all' ? 'All Leads' : `${filter[0].toUpperCase()}${filter.slice(1)} Leads`} · ${result.summary.estimatedPipelineValue}`}
          generatedAt={result.generatedAt}
          isLive={result.isLive}
          onRegenerate={regenerate}
          regenerating={regenerating}
          testId="pipeline-output"
        >
          <div className="grid grid-cols-4 gap-4 mb-6 pb-6 border-b border-[#E8E4DF]">
            <div><p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Total</p><p className="text-2xl text-[#1A1411] font-light">{result.summary.totalLeads}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Hot</p><p className="text-2xl text-[#A86A3A] font-light">{result.summary.breakdown.hot}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Warm</p><p className="text-2xl text-[#1A1411] font-light">{result.summary.breakdown.warm}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Cold</p><p className="text-2xl text-[#6B5B52] font-light">{result.summary.breakdown.cold}</p></div>
          </div>

          <Section label="Top Opportunities">
            <div className="space-y-3">
              {result.summary.topOpportunities.map((o, i) => (
                <div key={i} className="border border-[#E8E4DF] rounded-sm p-3">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <p className="font-medium text-[#1A1411]">{o.name}</p>
                    <span className="text-[10px] uppercase tracking-widest text-[#A86A3A] shrink-0">{o.stage} · {o.value}</span>
                  </div>
                  <p className="text-xs text-[#6B5B52]">{o.context}</p>
                </div>
              ))}
            </div>
          </Section>
          <Section label="Risks & Bottlenecks"><Bullets items={result.summary.risks} /></Section>
          <Section label="Recommended Actions"><Bullets items={result.summary.actions} /></Section>
        </OutputShell>
      )}

      <EmailGate
        open={emailGate}
        onClose={() => setEmailGate(false)}
        onSubmit={generate}
        context="AI Pipeline Summary"
      />
    </div>
  );
}

// ============================================================
// 3. AI INTERNAL TASK BRIEF
// ============================================================
function TaskBriefModule() {
  const [filter, setFilter] = useState('all');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailGate, setEmailGate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const generate = async (email) => {
    setLoading(true);
    setEmailGate(false);
    try {
      const r = await fetch(`${API_BASE}/public/showcase/task-brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filter, email }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      const data = await r.json();
      setTimeout(() => { setResult(data); setLoading(false); }, 800);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const handleClick = () => {
    const stored = getStoredEmail();
    if (stored) generate(stored);
    else setEmailGate(true);
  };

  const regenerate = async () => {
    const email = getStoredEmail();
    if (!email) return;
    setRegenerating(true);
    try {
      const r = await fetch(`${API_BASE}/public/showcase/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'tasks', key: filter, email }),
      });
      const data = await r.json();
      setResult(prev => ({ ...prev, brief: data.result, isLive: data.isLive, generatedAt: data.generatedAt }));
    } catch { /* ignore */ }
    setRegenerating(false);
  };

  return (
    <div data-testid="task-brief-module">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A86A3A] mb-2">
            <ListTodo className="w-3 h-3" /> Operational intelligence
          </div>
          <h3 className="text-2xl font-light text-[#1A1411]">AI Internal Task Brief</h3>
          <p className="text-[#6B5B52] text-sm mt-1">A complete operational scorecard — overdue items, upcoming deadlines and bottlenecks across the office.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setResult(null); }}
            className="appearance-none bg-white border border-[#E8E4DF] rounded-sm px-3 py-2.5 text-sm pr-8 text-[#1A1411] focus:outline-none focus:border-[#A86A3A]"
            data-testid="task-filter"
          >
            <option value="all">All Tasks</option>
            <option value="due-week">Due This Week</option>
            <option value="overdue">Overdue</option>
          </select>
          <button
            onClick={handleClick}
            disabled={loading}
            className="bg-[#1A1411] text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#A86A3A] transition-colors flex items-center gap-2 disabled:opacity-60"
            data-testid="task-generate-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <>Generate Task Brief <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>

      {!result && !loading && (
        <div className="border border-dashed border-[#E8E4DF] rounded-sm p-10 text-center bg-[#FAF8F5]">
          <ListTodo className="w-10 h-10 text-[#A86A3A]/40 mx-auto mb-3" />
          <p className="text-[#6B5B52] text-sm">Generate a task brief to see priority items, deadlines, and recommended actions.</p>
        </div>
      )}
      {loading && (
        <div className="border border-[#E8E4DF] rounded-sm p-10 text-center bg-white">
          <Loader2 className="w-7 h-7 text-[#A86A3A] mx-auto mb-3 animate-spin" />
          <p className="text-[#1A1411] font-medium">Generating task brief…</p>
        </div>
      )}
      {result && (
        <OutputShell
          title="Internal Task Summary"
          subtitle={`${result.brief.totalOutstanding} outstanding · ${result.brief.overdue} overdue · ${result.brief.dueThisWeek} due this week`}
          generatedAt={result.generatedAt}
          isLive={result.isLive}
          onRegenerate={regenerate}
          regenerating={regenerating}
          testId="task-brief-output"
        >
          <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-[#E8E4DF]">
            <div><p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Outstanding</p><p className="text-2xl text-[#1A1411] font-light">{result.brief.totalOutstanding}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Overdue</p><p className="text-2xl text-red-600 font-light">{result.brief.overdue}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-[#9B8B7E]">Due This Week</p><p className="text-2xl text-[#A86A3A] font-light">{result.brief.dueThisWeek}</p></div>
          </div>

          <Section label="Priority Tasks">
            <div className="space-y-3">
              {result.brief.priorityTasks.map((t, i) => (
                <div key={i} className="border border-[#E8E4DF] rounded-sm p-3">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <p className="font-medium text-[#1A1411]">{t.title}</p>
                    <span className={`text-[10px] uppercase tracking-widest shrink-0 ${t.due?.includes('overdue') ? 'text-red-600' : 'text-[#A86A3A]'}`}>{t.due}</span>
                  </div>
                  <p className="text-xs text-[#6B5B52]">{t.client} · Owner: {t.owner}</p>
                </div>
              ))}
            </div>
          </Section>
          <Section label="Operational Risks"><Bullets items={result.brief.operationalRisks} /></Section>
          <Section label="Upcoming Deadlines"><Bullets items={result.brief.upcomingDeadlines} /></Section>
          <Section label="Recommended Actions"><Bullets items={result.brief.actions} /></Section>
        </OutputShell>
      )}

      <EmailGate
        open={emailGate}
        onClose={() => setEmailGate(false)}
        onSubmit={generate}
        context="AI Internal Task Brief"
      />
    </div>
  );
}

// ============================================================
// MAIN SUITE WRAPPER
// ============================================================
export default function AIInsightsSuite() {
  return (
    <section className="bg-[#FAF8F5] py-20 lg:py-28 border-y border-[#E8E4DF]" id="ai-insights" data-testid="ai-insights-suite">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#A86A3A] mb-4">
            <Sparkles className="w-3 h-3" /> Live AI demo · No login required
          </div>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1A1411] mb-4 leading-tight">
            See what your office could ship in <span className="text-[#A86A3A]">90 seconds</span>.
          </h2>
          <p className="text-[#6B5B52] text-lg leading-relaxed">
            Three live modules powered by your client data, your pipeline, and your team's tasks. Each one replaces an hour of manual prep — try them now on three curated showcase families.
          </p>
        </div>

        {/* HERO: Meeting Brief */}
        <div className="mb-24">
          <MeetingBriefModule />
        </div>

        {/* Supporting #1: Pipeline */}
        <div className="border-t border-[#E8E4DF] pt-16 mb-16">
          <PipelineModule />
        </div>

        {/* Supporting #2: Tasks */}
        <div className="border-t border-[#E8E4DF] pt-16">
          <TaskBriefModule />
        </div>

        {/* Bottom signpost */}
        <div className="mt-20 pt-12 border-t border-[#E8E4DF] flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#A86A3A] flex-shrink-0 mt-0.5" />
            <p className="text-[#3D3530] leading-relaxed max-w-2xl">
              Inside the platform, all three briefs run on <strong>your</strong> live data — clients, leads, tasks, and service lines. Replaces hours of manual work across the entire office.
            </p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Link to="/pilot" className="inline-flex items-center gap-2 bg-[#A86A3A] text-white px-5 py-3 rounded-sm font-medium hover:bg-[#8B5730] transition-colors text-sm">
              Request a Pilot <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-[#A86A3A] text-[#A86A3A] px-5 py-3 rounded-sm font-medium hover:bg-[#A86A3A]/5 transition-colors text-sm">
              Book a Walkthrough
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
